import React from 'react';
import { BaseModal } from '../../ui/atoms/Modal/Modal';

// =============================================================================
// THUMBNAIL GALLERY MODAL - Full screen view of thumbnails
// =============================================================================

export const ThumbnailGalleryModal = ({ 
  isOpen, 
  onClose, 
  thumbnails, 
  currentIndex,
  onSelectThumbnail,
  onSetAsMain,
  onDelete
}) => {
  if (!thumbnails || thumbnails.length === 0) return null;

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} size="xl">
      <div className="bg-white">
        {/* Header */}
        <div className="border-b border-gray-200 px-6 py-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Thumbnail Gallery ({thumbnails.length} images)
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Click on an image to select it, or use the actions below each thumbnail
          </p>
        </div>

        {/* Gallery Grid */}
        <div className="px-6 py-6 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {thumbnails.map((thumb, index) => (
              <div 
                key={thumb.id || index}
                className={`relative group cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${
                  index === currentIndex 
                    ? 'border-primary-600 ring-2 ring-primary-200' 
                    : 'border-gray-200 hover:border-gray-400'
                }`}
                onClick={() => onSelectThumbnail(index)}
              >
                {/* Image */}
                <div className="aspect-video bg-gray-100">
                  <img 
                    src={thumb.url} 
                    alt={thumb.alt || `Thumbnail ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Chosen Badge */}
                {thumb.chosen && (
                  <div className="absolute top-2 left-2 bg-green-500 text-white px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    MAIN
                  </div>
                )}

                {/* Current Selection Badge */}
                {index === currentIndex && (
                  <div className="absolute top-2 right-2 bg-primary-600 text-white px-2 py-1 rounded-full text-xs font-medium">
                    Selected
                  </div>
                )}

                {/* Hover Overlay with Actions */}
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-60 transition-all flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                  {!thumb.chosen && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onSetAsMain(index);
                      }}
                      className="bg-white text-gray-900 px-3 py-1.5 rounded-md text-sm font-medium hover:bg-gray-100 transition-colors"
                    >
                      Set as Main
                    </button>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(index);
                    }}
                    className="bg-red-600 text-white px-3 py-1.5 rounded-md text-sm font-medium hover:bg-red-700 transition-colors"
                  >
                    Delete
                  </button>
                </div>

                {/* Info Overlay */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-3 text-white text-xs">
                  <p className="font-medium truncate">{thumb.photographer || 'Unknown'}</p>
                  <p className="text-gray-300 text-[10px]">{thumb.width} × {thumb.height}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              {thumbnails.filter(t => t.chosen).length > 0 ? (
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                  Main thumbnail selected
                </span>
              ) : (
                <span className="text-amber-600">⚠️ No main thumbnail selected</span>
              )}
            </div>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors font-medium"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </BaseModal>
  );
};

// =============================================================================
// DELETE CONFIRMATION MODAL
// =============================================================================

export const ThumbnailDeleteModal = ({ 
  isOpen, 
  onClose, 
  onConfirm,
  thumbnail,
  isDeleting 
}) => {
  if (!thumbnail) return null;

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} size="sm">
      <div className="bg-white">
        {/* Icon */}
        <div className="px-6 pt-6 pb-4">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
            <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
            </svg>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 pb-4">
          <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
            Delete Thumbnail?
          </h3>
          <p className="text-sm text-gray-600 text-center mb-4">
            Are you sure you want to delete this thumbnail? This action cannot be undone.
          </p>
          
          {/* Thumbnail Preview */}
          <div className="bg-gray-100 rounded-lg p-2 mb-4">
            <img 
              src={thumbnail.url} 
              alt={thumbnail.alt || 'Thumbnail'}
              className="w-full h-32 object-cover rounded"
            />
            <p className="text-xs text-gray-600 mt-2 text-center">
              by {thumbnail.photographer || 'Unknown'}
            </p>
          </div>

          {thumbnail.chosen && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-amber-800 flex items-center gap-2">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                This is your main thumbnail. The first remaining thumbnail will become the new main.
              </p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="bg-gray-50 px-6 py-4 flex gap-3 justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={isDeleting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isDeleting}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isDeleting ? 'Deleting...' : 'Delete Thumbnail'}
          </button>
        </div>
      </div>
    </BaseModal>
  );
};

// =============================================================================
// SET AS MAIN CONFIRMATION MODAL
// =============================================================================

export const ThumbnailSetMainModal = ({ 
  isOpen, 
  onClose, 
  onConfirm,
  thumbnail,
  isSetting 
}) => {
  if (!thumbnail) return null;

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} size="sm">
      <div className="bg-white">
        {/* Icon */}
        <div className="px-6 pt-6 pb-4">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
            <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 pb-4">
          <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
            Set as Main Thumbnail?
          </h3>
          <p className="text-sm text-gray-600 text-center mb-4">
            This thumbnail will be used as the primary image for your article.
          </p>
          
          {/* Thumbnail Preview */}
          <div className="bg-gray-100 rounded-lg p-2">
            <img 
              src={thumbnail.url} 
              alt={thumbnail.alt || 'Thumbnail'}
              className="w-full h-32 object-cover rounded"
            />
            <p className="text-xs text-gray-600 mt-2 text-center">
              by {thumbnail.photographer || 'Unknown'}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="bg-gray-50 px-6 py-4 flex gap-3 justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={isSetting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isSetting}
            className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSetting ? 'Setting...' : 'Set as Main'}
          </button>
        </div>
      </div>
    </BaseModal>
  );
};

